import { alpha, createTheme } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';

import defaultTheme from '../scss/_defaultTheme.module.scss';
import { ColorProps } from 'types';

/**
 * How much of a §13.3 role's own ink washes its dark surface. See `powerDial`
 * below for why it is a wash rather than a level, and why 16% is a ceiling.
 */
const DARK_ROLE_WASH = 0.16;

const Palette = (navType: PaletteMode) => {
  let colors: ColorProps = defaultTheme;
  const isDark = navType === 'dark';

  return createTheme({
    palette: {
      mode: navType,
      common: {
        black: colors.darkPaper
      },
      primary: {
        50: colors.primary50,
        100: colors.primary100,
        200: colors.primary200,
        300: colors.primary300,
        400: colors.primary400,
        500: colors.primary500,
        600: colors.primary600,
        700: colors.primary700,
        800: colors.primary800,
        900: colors.primary900,
        A100: colors.primary25,
        main: colors.primary600,
        light: colors.primary100
      },
      secondary: {
        50: colors.secondary50,
        100: colors.secondary100,
        200: colors.secondary200,
        300: colors.secondary300,
        400: colors.secondary400,
        500: colors.secondary500,
        600: colors.secondary600,
        700: colors.secondary700,
        800: colors.secondary800,
        900: colors.secondary900,
        main: colors.secondary600,
        light: colors.secondary50
      },
      error: {
        50: colors.error50,
        100: colors.error100,
        200: colors.error200,
        300: colors.error300,
        400: colors.error400,
        500: colors.error500,
        600: colors.error500,
        700: colors.error700,
        main: colors.error700,
        light: colors.error100,
        dark: colors.error700
      },
      warning: {
        50: colors.warning50,
        100: colors.warning100,
        200: colors.warning200,
        300: colors.warning300,
        400: colors.warning400,
        500: colors.warning200,
        600: colors.warning700,
        700: colors.warning700,
        main: colors.warning700,
        light: colors.warning100,
        dark: colors.warning700
      },
      success: {
        50: colors.success50,
        100: colors.success100,
        200: colors.success200,
        300: colors.success300,
        400: colors.success400,
        500: colors.success500,
        600: colors.success700,
        700: colors.success700,
        800: colors.success800,
        main: colors.success700,
        light: colors.success100,
        dark: colors.success700
      },
      grey: {
        50: colors.grey50,
        100: colors.grey100,
        200: colors.grey200,
        300: colors.grey300,
        400: colors.grey400,
        500: colors.grey500,
        600: colors.grey600,
        700: colors.grey700,
        800: colors.grey800,
        900: colors.grey900
      },
      dark: {
        light: colors.darkTextPrimary,
        main: colors.darkLevel1,
        dark: colors.darkLevel2,
        800: colors.darkBackground,
        900: colors.darkPaper
      },
      text: {
        primary: navType === 'dark' ? colors.darkTextPrimary : colors.grey900,
        secondary: navType === 'dark' ? colors.darkTextSecondary : colors.grey600,
        dark: navType === 'dark' ? colors.darkTextPrimary : colors.grey900,
        hint: colors.grey100
      },
      divider: navType === 'dark' ? colors.darkBorder : colors.grey200,
      background: {
        paper: navType === 'dark' ? colors.darkLevel2 : colors.paper,
        default: navType === 'dark' ? colors.darkPaper : colors.paper
      },
      chipBackground: {
        primary: colors.primary100,
        secondary: colors.secondary100,
        error: colors.error100,
        warning: colors.warning100,
        success: colors.success100,
        grey: colors.grey100
      },
      chipLabelColor: {
        primary: colors.primary500,
        secondary: colors.secondary500,
        error: colors.error400,
        warning: colors.warning400,
        success: '#00c853',
        grey: colors.grey400
      },
      /**
       * §13.3 — colour IS the outcome, one rule the loan officer learns once and
       * reads on the rail, the history chips, the stat tiles, the pill and the
       * CRM wrap-up card. Five roles, no sixth.
       *
       * A ROLE IS A PAIR, not a colour. "Passes 4.5:1 against its own surface"
       * is only a checkable claim if the surface is part of the token, and the
       * three roles that were failing were failing precisely because the ink was
       * picked from one place and the background from another. Consumers set
       * both from here or neither.
       *
       * Measured, both themes (WCAG 2.1 relative luminance; the dark column is
       * the worst of the two bases the wash below composites over):
       *   dialing    6.11:1 light · 6.62:1 dark
       *   connected  5.85:1 light · 7.31:1 dark
       *   abandon    6.71:1 light · 6.83:1 dark
       *   noanswer   5.30:1 light · 4.66:1 dark
       *   danger     7.15:1 light · 6.14:1 dark
       *
       * `noanswer` is §13.3's muted register, not only the no-answer leg state:
       * it is also the surface-plus-ink for every quiet card in the feature (the
       * pre-call context note, the spectator notice). That is the pairing the
       * audit caught at 3.11:1 — `text.secondary` (#727275) is fine on white and
       * fails on `grey[200]`, and nothing in the theme said so.
       *
       * THE DARK SURFACES ARE A WASH OF THE INK, NOT `dark[800]`. That was the
       * obvious first answer and it is wrong: every dark card in the feature IS
       * `dark[800]` — the rail cards, the completed stack, the call-log rows —
       * so a chip painted `dark[800]` would be an invisible chip on its own
       * card. A 16% wash of the role's own ink lifts it off the card (~1.5:1,
       * enough to read as a pill) while keeping the label at 4.66:1 or better
       * over either dark base this feature paints on (`dark[800]` cards and
       * `darkLevel2` paper). 16% is a ceiling, not a taste: at 22% the muted
       * role drops to 4.14:1 on a rail card and fails.
       *
       * Derived rather than declared, so the five dark surfaces cannot drift
       * from the five dark inks — and there is no sixth palette of tints to
       * maintain, which is the fork §13.3 is guarding against.
       */
      powerDial: {
        dialing: {
          ink: isDark ? colors.primary200 : colors.primary600,
          surface: isDark ? alpha(colors.primary200, DARK_ROLE_WASH) : colors.primary50
        },
        connected: {
          ink: isDark ? colors.success200 : colors.pdConnectedInk,
          surface: isDark ? alpha(colors.success200, DARK_ROLE_WASH) : colors.success50
        },
        abandon: {
          ink: isDark ? colors.warning500 : colors.pdAbandonInk,
          surface: isDark ? alpha(colors.warning500, DARK_ROLE_WASH) : colors.warning50
        },
        noanswer: {
          ink: isDark ? colors.darkTextSecondary : colors.grey700,
          surface: isDark ? alpha(colors.darkTextSecondary, DARK_ROLE_WASH) : colors.grey200
        },
        danger: {
          ink: isDark ? colors.error200 : colors.error700,
          surface: isDark ? alpha(colors.error200, DARK_ROLE_WASH) : colors.error50
        }
      },
      /**
       * Lead grade tiers, built from the same {ink, surface} shape and the same
       * dark wash as `powerDial` above rather than a second set of tints.
       *
       * D is grey, not red: D means cold, not bad. Red belongs to exactly one
       * thing in this feature — a contact who asked not to be contacted at all
       * — and spending it on the bottom tier would make four fifths of a book
       * look like a problem. The tier letter is always rendered alongside these,
       * so colour is never the only channel carrying the tier.
       */
      leadTier: {
        A: {
          ink: isDark ? colors.success200 : colors.pdConnectedInk,
          surface: isDark ? alpha(colors.success200, DARK_ROLE_WASH) : colors.success50
        },
        B: {
          ink: isDark ? colors.primary200 : colors.primary600,
          surface: isDark ? alpha(colors.primary200, DARK_ROLE_WASH) : colors.primary50
        },
        C: {
          ink: isDark ? colors.warning500 : colors.pdAbandonInk,
          surface: isDark ? alpha(colors.warning500, DARK_ROLE_WASH) : colors.warning50
        },
        D: {
          ink: isDark ? colors.darkTextSecondary : colors.grey700,
          surface: isDark ? alpha(colors.darkTextSecondary, DARK_ROLE_WASH) : colors.grey200
        }
      }
    }
  });
};

export default Palette;
